/**
 * Composant d'affichage de l'évaluation globale d'une réservation
 * Affiche la note avec des étoiles pour les réservations terminées
 * Cliquable pour ouvrir le modal d'édition de la review
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Rating,
  Chip,
  CircularProgress,
  Tooltip,
  alpha,
  Stack,
  IconButton
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  RateReview as ReviewIcon,
  Edit as EditIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ApprovedIcon,
  Block as RejectedIcon,
  Warning as ChangesRequestedIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ReservationReviewModal from './ReservationReviewModal';

/**
 * Composant ReservationRatingDisplay
 * @param {Object} props
 * @param {Object} props.reservation - L'objet réservation
 * @param {boolean} props.compact - Mode compact (petite taille)
 * @param {boolean} props.showLabel - Afficher le label "Évaluation"
 * @param {boolean} props.editable - Permettre l'édition (ouvrir modal au clic)
 * @param {Function} props.onReviewUpdated - Callback après mise à jour review
 * @returns {React.ReactNode}
 */
const moderationStatusConfig = {
  pending: {
    label: 'moderation.pending',
    color: 'warning',
    icon: PendingIcon
  },
  approved: {
    label: 'moderation.approved',
    color: 'success',
    icon: ApprovedIcon
  },
  rejected: {
    label: 'moderation.rejected',
    color: 'error',
    icon: RejectedIcon
  },
  changes_requested: {
    label: 'moderation.changes_requested',
    color: 'info',
    icon: ChangesRequestedIcon
  }
};

const ReservationRatingDisplay = ({ 
  reservation, 
  compact = false,
  showLabel = true,
  editable = true,
  onReviewUpdated
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['reviews', 'renterEvaluation']);
  const [rating, setRating] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const getModerationStatus = () => {
    if (!reviewData) return 'approved';
    return reviewData.moderation_status || 'approved';
  };

  useEffect(() => {
    // Ne rien afficher si la réservation n'est pas complétée
    if (reservation?.status !== 'completed') {
      return;
    }

    // Vérifier si les données de review sont déjà dans l'objet réservation
    if (reservation?.reservationReview) {
      setRating(reservation.reservationReview.overall_rating);
      setReviewData(reservation.reservationReview);
      setLoading(false);
      return;
    }

    // Fallback: Si review_data existe (ancien format)
    if (reservation?.review_data) {
      setRating(reservation.review_data.overall_rating);
      setReviewData(reservation.review_data);
      setLoading(false);
      return;
    }

    // Pas de review trouvée
    setRating(null);
    setReviewData(null);
    setLoading(false);
  }, [reservation]);

  const handleClick = async () => {
    if (!editable || !reviewData) return;
    
    try {
      // Charger la review complète avec tous les détails depuis l'API
      const response = await api.get(`/reviews/reservations/${reservation.id}`);
      
      // La réponse est {success, message, data: review}
      // Donc l'objet review est dans response.data.data
      const fullReviewData = response.data.data;
      
      console.log('📥 Review complète chargée:', fullReviewData);
      console.log('� Vérification des champs détaillés:');
      console.log('  - overall_rating:', fullReviewData.overall_rating);
      console.log('  - equipment_quality_rating:', fullReviewData.equipment_quality_rating);
      console.log('  - process_pickup_rating:', fullReviewData.process_pickup_rating);
      console.log('  - experience_value_rating:', fullReviewData.experience_value_rating);
      console.log('  - positive_tags:', fullReviewData.positive_tags);
      
      // Mettre à jour avec les données complètes
      setReviewData(fullReviewData);
      setModalOpen(true);
    } catch (error) {
      console.error('❌ Erreur chargement review complète:', error);
      // En cas d'erreur, ouvrir quand même avec les données partielles
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    console.log('🚪 handleModalClose appelé dans RatingDisplay');
    setModalOpen(false);
  };

  const handleReviewUpdated = (updatedReview) => {
    console.log('✅ handleReviewUpdated appelé avec:', updatedReview);
    setRating(updatedReview.overall_rating);
    setReviewData(updatedReview);
    if (onReviewUpdated) {
      onReviewUpdated(updatedReview);
    }
  };

  // Ne rien afficher si pas completed
  if (reservation?.status !== 'completed') {
    return null;
  }

  // Affichage pendant le chargement
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: compact ? 0.5 : 1
        }}
      >
        <CircularProgress size={compact ? 16 : 20} />
      </Box>
    );
  }

  // Si erreur (autre que 404)
  if (error) {
    return null;
  }

  // Si pas de note disponible
  if (!rating) {
    return null;
  }

  // Mode compact
  if (compact) {
    return (
      <>
        <Tooltip 
          title={editable ? t('ratingDisplay.clickToEdit', 'Cliquer pour modifier') : t('ratingDisplay.tooltip', { rating: rating.toFixed(1) })} 
          arrow
        >
          <Chip
            icon={<StarIcon sx={{ fontSize: 14 }} />}
            label={rating.toFixed(1)}
            size="small"
            onClick={editable ? handleClick : undefined}
            sx={{
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.dark,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
              cursor: editable ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '& .MuiChip-icon': {
                color: theme.palette.warning.main
              },
              ...(editable && {
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.2),
                  transform: 'scale(1.05)'
                }
              })
            }}
          />
        </Tooltip>
        
        {editable && reviewData && (
          <ReservationReviewModal
            open={modalOpen}
            onClose={handleModalClose}
            reservation={{ ...reservation, reservationReview: reviewData }}
            existingReview={reviewData}
            editMode={true}
            onReviewSubmitted={handleReviewUpdated}
          />
        )}
      </>
    );
  }

  // Mode normal avec étoiles
  return (
    <>
      <Box
        onClick={editable ? handleClick : undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          p: 1,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.warning.main, 0.08),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          cursor: editable ? 'pointer' : 'default',
          transition: 'all 0.2s',
          position: 'relative',
          ...(editable && {
            '&:hover': {
              backgroundColor: alpha(theme.palette.warning.main, 0.15),
              borderColor: alpha(theme.palette.warning.main, 0.4),
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2]
            }
          })
        }}
      >
        {editable && (
          <Tooltip title={t('ratingDisplay.clickToEdit', 'Cliquer pour modifier')} arrow placement="top">
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                opacity: 0.6,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: alpha(theme.palette.warning.main, 0.2)
                }
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        
        {showLabel && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              letterSpacing: 0.5
            }}
          >
            {t('ratingDisplay.label')}
          </Typography>
        )}
        
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Rating
            value={rating}
            precision={0.5}
            readOnly
            size="small"
            icon={<StarIcon sx={{ fontSize: 18 }} />}
            emptyIcon={<StarBorderIcon sx={{ fontSize: 18 }} />}
            sx={{
              '& .MuiRating-iconFilled': {
                color: theme.palette.warning.main
              },
              '& .MuiRating-iconEmpty': {
                color: alpha(theme.palette.warning.main, 0.3)
              }
            }}
          />
          
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: theme.palette.warning.dark,
              fontSize: '0.9rem',
              ml: 0.5
            }}
          >
            {rating.toFixed(1)}
          </Typography>
        </Stack>
        
        {/* Badge de statut de modération */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ReviewIcon sx={{ fontSize: 10, color: theme.palette.text.secondary }} />
          {(() => {
            const status = getModerationStatus();
            const config = moderationStatusConfig[status];
            const StatusIcon = config.icon;
            return (
              <Chip
                size="small"
                icon={<StatusIcon sx={{ fontSize: 12 }} />}
                label={t(`renterEvaluation:${config.label}`)}
                color={config.color}
                variant="outlined"
                sx={{ 
                  fontWeight: 500, 
                  fontSize: '0.65rem',
                  height: 18,
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            );
          })()}
        </Box>
      </Box>
      
      {editable && reviewData && (
        <ReservationReviewModal
          open={modalOpen}
          onClose={handleModalClose}
          reservation={{ ...reservation, reservationReview: reviewData }}
          existingReview={reviewData}
          editMode={true}
          onReviewSubmitted={handleReviewUpdated}
        />
      )}
    </>
  );
};

export default ReservationRatingDisplay;
